import { useState, useEffect } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { ScrollText, Pencil, Save, X, Copy, ExternalLink, Globe } from "lucide-react";
import { LegalText } from "@/components/LegalText";

const EDIT_RANKS = ["Director", "Captain", "Lieutenant", "Sergeant II"];

export default function Gefaengnisordnung() {
  const { user } = useAuth();
  const canEdit = user?.name === "Administration" || EDIT_RANKS.includes(user?.rang);
  const [text, setText] = useState("");
  const [draft, setDraft] = useState("");
  const [meta, setMeta] = useState({ updated_by: "", updated_at: "" });
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const publicUrl = `${window.location.origin}/public/gefaengnisordnung`;

  const load = () => {
    api.get("/gefaengnisordnung").then((r) => {
      setText(r.data.text || "");
      setMeta({ updated_by: r.data.updated_by || "", updated_at: r.data.updated_at || "" });
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const startEdit = () => { setDraft(text); setEditing(true); };
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/gefaengnisordnung", { text: draft });
      setText(data.text || "");
      setMeta({ updated_by: data.updated_by || "", updated_at: data.updated_at || "" });
      setEditing(false);
      toast.success("Gefängnisordnung gespeichert");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };
  const copyLink = () => {
    navigator.clipboard?.writeText(publicUrl);
    toast.success("Öffentlicher Link kopiert");
  };

  return (
    <div>
      <PageHeader
        eyebrow="SADCR · Öffentlich einsehbar"
        title="Gefängnisordnung"
        description="Öffentlich lesbar – ohne Login. Bearbeitung durch Leitung (Director – Sergeant II)."
      />
      <div className="mt-6 space-y-4">
        <div className="flex flex-wrap items-center gap-3 border border-zinc-800 rounded-sm bg-[#0C0E12] px-4 py-3">
          <Globe className="h-4 w-4 text-primary shrink-0" />
          <span className="text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-500">Öffentlicher Link:</span>
          <a href={publicUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline text-sm inline-flex items-center gap-1 break-all">
            {publicUrl} <ExternalLink className="h-3.5 w-3.5" />
          </a>
          <Button data-testid="geford-copy-link" size="sm" variant="outline" onClick={copyLink} className="rounded-sm border-zinc-700 gap-2 ml-auto">
            <Copy className="h-3.5 w-3.5" /> Kopieren
          </Button>
        </div>

        {canEdit && !editing && (
          <div className="flex justify-end">
            <Button data-testid="geford-edit" size="sm" onClick={startEdit} className="rounded-sm bg-primary hover:bg-blue-600 text-white gap-2">
              <Pencil className="h-3.5 w-3.5" /> Bearbeiten
            </Button>
          </div>
        )}

        <div className="border border-zinc-800 rounded-sm bg-[#0C0E12] p-6">
          {editing ? (
            <div className="space-y-4">
              <textarea
                data-testid="geford-textarea"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                rows={24}
                className="w-full bg-transparent border border-zinc-700 rounded-sm p-4 text-sm text-zinc-200 focus:outline-none focus:ring-1 focus:ring-primary leading-relaxed"
                placeholder="Text der Gefängnisordnung…"
              />
              <div className="flex gap-2">
                <Button data-testid="geford-save" size="sm" onClick={save} disabled={saving} className="rounded-sm bg-primary hover:bg-blue-600 text-white gap-2">
                  <Save className="h-3.5 w-3.5" /> {saving ? "Speichern…" : "Speichern"}
                </Button>
                <Button data-testid="geford-cancel" size="sm" variant="ghost" onClick={() => setEditing(false)} className="rounded-sm text-zinc-400 gap-2">
                  <X className="h-3.5 w-3.5" /> Abbrechen
                </Button>
              </div>
            </div>
          ) : text ? (
            <div data-testid="geford-content"><LegalText text={text} /></div>
          ) : (
            <div className="flex flex-col items-center gap-2 py-16 text-zinc-600">
              <ScrollText className="h-8 w-8" />
              <span className="font-mono text-sm">Noch kein Text hinterlegt.</span>
            </div>
          )}
          {meta.updated_at && !editing && (
            <div className="mt-6 pt-4 border-t border-zinc-800 font-mono text-[10px] uppercase tracking-[0.12em] text-zinc-600">
              Zuletzt aktualisiert: {new Date(meta.updated_at).toLocaleString("de-DE")}{meta.updated_by ? ` · ${meta.updated_by}` : ""}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
